import type { ClipboardEvent, JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { App } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { hubApi, clientContext, type HubSeverity } from '../../lib/api/hub';
import {
  FB_SEVERITY,
  FEEDBACK_FILES_MAX,
  crmFeedbackApi,
  crmSection,
  fileProblem,
  titleFromBody,
  type FeedbackSeverity,
} from '../../lib/feedback';
import { pageTitleOf } from '../../lib/nav';
import { previewFooterText, routingTextOf } from '../../lib/support-format';
import { clearFeedbackDraft, feedbackDraftKey, readFeedbackDraft, writeFeedbackDraft } from '../../lib/feedback-draft';
import { canCaptureScreen, captureScreen, shotFileName } from './feedback-capture';
import { IconCamera, IconPaperclip, IconSend, IconX } from './feedback-icons';
import { ROUTE_PREVIEW_MIN_CHARS, RoutingCard, useRoutePreview } from './QuestionRouting';
import { ScreenshotAnnotator } from './ScreenshotAnnotator';

export type FormKind = 'bug' | 'idea' | 'question';

/** Кто пишет: CRM-пользователь (ручки /api/feedback*) или сотрудник iiko (прокси HUB, один файл, без разбора по отделам). */
export type FeedbackMode = 'crm' | 'iiko';

/** Что отправили: код обращения и (для вопроса в УК) куда он ушёл — по ответу сервера, не по предпросмотру. */
export interface SentInfo {
  id: string;
  code: string;
  question?: { deptLabel: string; assigneeName: string | null; dueAt: string | null; urgent: boolean; slaMinutes?: number | null };
}

/** Шкала CRM («Насколько мешает») → шкала прокси HUB для сотрудника iiko. */
const HUB_SEVERITY: Record<FeedbackSeverity, HubSeverity> = { block: 'block', annoy: 'high', minor: 'normal' };

const LABEL: Record<FormKind, string> = { bug: 'Что случилось', idea: 'Идея', question: 'Вопрос' };
const PLACEHOLDER: Record<FormKind, string> = {
  bug: 'Например: не открывается PDF на Диске — кнопка крутится и ничего…',
  idea: 'Например: добавить видео сборки Калифорнии',
  question: 'Например: нужен доступ к разделу «Финансы» для управляющего',
};
const EMPTY_WARNING: Record<FormKind, string> = { bug: 'Опишите, что случилось', idea: 'Опишите идею', question: 'Напишите вопрос' };

/** Миниатюра выбранного файла: картинка — превью, PDF — значок. */
function FileRow({ file, onRemove }: { file: File; onRemove: () => void }): JSX.Element {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!file.type.startsWith('image/')) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <div className="fk-hub-fb-file">
      {src ? <img src={src} alt="" className="fk-hub-fb-file-thumb" /> : <IconPaperclip size={14} />}
      <span className="fk-hub-fb-file-name">{file.name}</span>
      <button type="button" className="fk-hub-fb-file-x" aria-label="Убрать файл" onClick={onRemove}>
        <IconX size={12} />
      </button>
    </div>
  );
}

export function FeedbackForm({
  kind,
  mode,
  roleName,
  full,
  sla,
  onCapture = captureScreen,
  onSent,
  draftOwner = null,
}: {
  kind: FormKind;
  mode: FeedbackMode;
  roleName: string | null;
  /** Чей черновик (id или почта зрителя) — ключ в localStorage; без него — общий «anon» на этот браузер. */
  draftOwner?: string | null;
  full: boolean;
  /** «Ответим в течение рабочего дня» — подпись у кнопки «Отправить». */
  sla: string;
  /**
   * Сделать снимок экрана. Плавающий «+» (HelpFab) на время снимка прячет окно формы,
   * чтобы оно не попало в кадр; без него снимок делается сразу.
   */
  onCapture?: () => Promise<string | null>;
  onSent: (r: SentInfo) => void;
}): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { pathname } = useLocation();
  // Страница, откуда пишут, — на момент открытия формы (виджет живёт поверх неё).
  const where = useMemo(() => ({ page: pageTitleOf(pathname), path: pathname }), [pathname]);

  // Черновик (03.11.2026, как в CRM): текст формы живёт в localStorage, пока обращение не отправлено, —
  // закрытие шторки, «Назад» или обновление страницы его не теряют.
  const draftKey = feedbackDraftKey(draftOwner, kind);
  const draft = useMemo(() => readFeedbackDraft(draftKey), [draftKey]);
  const [body, setBody] = useState(draft?.body ?? '');
  const [expected, setExpected] = useState(draft?.expected ?? '');
  const [restored] = useState(!!draft);
  const sentRef = useRef(false);
  const textRef = useRef({ body: '', expected: '' });
  const hintRef = useRef(message);
  hintRef.current = message;
  useEffect(() => {
    textRef.current = { body, expected };
    writeFeedbackDraft(draftKey, { body, expected });
  }, [body, expected, draftKey]);
  // Форму закрыли (крестик, «Назад», Esc) с неотправленным текстом — скажем прямо, что обращение не ушло.
  // Эффект с пустыми deps и refs: любая зависимость дала бы ложное сообщение при перерисовке.
  useEffect(
    () => () => {
      if (sentRef.current || !textRef.current.body.trim()) return;
      hintRef.current.info('Обращение не отправлено. Текст сохранён как черновик — откройте форму снова и нажмите «Отправить»', 6);
    },
    [],
  );
  const [sev, setSev] = useState<FeedbackSeverity>('annoy');
  const [files, setFiles] = useState<File[]>([]);
  // Снимок экрана: картинка для обводки (data-URL) и итоговый файл с рамками — он занимает одно место среди вложений.
  const [shotSrc, setShotSrc] = useState<string | null>(null);
  const [shotBlob, setShotBlob] = useState<Blob | null>(null);
  const [shooting, setShooting] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const maxFiles = mode === 'iiko' ? 1 : FEEDBACK_FILES_MAX;
  const shotCount = shotSrc ? 1 : 0;
  const noSlots = files.length + shotCount >= maxFiles;

  // Вопрос в УК у CRM-пользователя: отдел, выбранный вручную («Не туда?»), и предпросмотр «Кому уйдёт вопрос».
  const [manualDept, setManualDept] = useState<string | null>(null);
  const routed = mode === 'crm' && kind === 'question';
  const previewText = routed ? routingTextOf(null, body) : '';
  const preview = useRoutePreview(previewText, manualDept);
  // Текст стал короче порога — карточка «Кому уйдёт вопрос» исчезла вместе с кнопкой «Определить отдел автоматически»,
  // поэтому и ручной выбор сбрасываем: иначе он остался бы невидимым и ушёл бы с вопросом.
  useEffect(() => {
    if (manualDept && previewText.length < ROUTE_PREVIEW_MIN_CHARS) setManualDept(null);
  }, [manualDept, previewText]);

  const addFiles = (list: File[]): void => {
    const ok: File[] = [];
    for (const f of list) {
      const problem = fileProblem(f);
      if (problem) message.warning(problem);
      else ok.push(f);
    }
    if (!ok.length) return;
    if (files.length + shotCount + ok.length > maxFiles) {
      message.warning(mode === 'iiko' ? 'Можно приложить один файл' : `Можно приложить не больше ${maxFiles} файлов`);
    }
    setFiles((x) => [...x, ...ok].slice(0, maxFiles - shotCount));
  };

  // «Сделать снимок экрана»: окно формы на это время прячется (onCapture), потом — обводка.
  const takeShot = async (): Promise<void> => {
    setShooting(true);
    try {
      const shot = await onCapture();
      if (shot) setShotSrc(shot);
      else message.info('Снимок не сделан — можно загрузить картинку файлом');
    } finally {
      setShooting(false);
    }
  };

  // Ctrl+V: картинка из буфера (скриншот) прикладывается как файл.
  const onPaste = (e: ClipboardEvent<HTMLDivElement>): void => {
    const imgs = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
    if (!imgs.length) return;
    addFiles(imgs.map((f, i) => new File([f], `снимок-экрана${i ? `-${i + 1}` : ''}.${f.type === 'image/jpeg' ? 'jpg' : f.type === 'image/webp' ? 'webp' : 'png'}`, { type: f.type })));
  };

  const submit = async (): Promise<void> => {
    if (!body.trim()) {
      message.warning(EMPTY_WARNING[kind]);
      return;
    }
    if (shotSrc && !shotBlob) {
      message.info('Снимок ещё готовится — нажмите «Отправить» через секунду');
      return;
    }
    // Снимок с рамками уходит тем же путём, что загруженный файл: обычный File (PNG, при большом весе JPEG).
    const shotFile = shotSrc && shotBlob ? new File([shotBlob], shotFileName(shotBlob.type), { type: shotBlob.type }) : null;
    const shotProblem = shotFile ? fileProblem(shotFile) : null;
    if (shotProblem) {
      message.warning(`${shotProblem}. Уберите снимок или загрузите картинку файлом`);
      return;
    }
    const attachments = (shotFile ? [shotFile, ...files] : files).slice(0, maxFiles);
    setBusy(true);
    try {
      const pageUrl = `${window.location.origin}${where.path}`;
      if (mode === 'iiko') {
        // Сотрудник iiko: прокси HUB одним запросом (один файл). «Что ожидали» уходит в текст.
        const text = kind === 'bug' && expected.trim() ? `${body.trim()}\n\nЧто ожидали: ${expected.trim()}` : body.trim();
        const r = await hubApi.sendFeedback(
          {
            kind: kind === 'question' ? 'request' : kind,
            title: titleFromBody(body),
            body: text,
            severity: kind === 'bug' ? HUB_SEVERITY[sev] : null,
            page: where.page,
            pageUrl,
            file: attachments[0] ?? null,
          },
          { type: 'iiko_employee', roleName },
        );
        if (r.attachError) message.warning(`Файл не приложился: ${r.attachError}`);
        void qc.invalidateQueries({ queryKey: ['wiki', 'hub', 'feedback'] });
        sentRef.current = true;
        clearFeedbackDraft(draftKey);
        onSent({ id: r.id, code: r.code });
        return;
      }

      const section = crmSection(where.page);
      const r = await crmFeedbackApi.create({
        kind,
        body: body.trim(),
        expected: kind === 'bug' ? expected.trim() || undefined : undefined,
        severity: kind === 'bug' ? sev : undefined,
        section,
        pageUrl,
        context: clientContext(section, roleName),
        // Отдел уходит, только если ручной выбор виден в карточке (предпросмотр ответил routedBy: 'manual');
        // иначе его определит бэкенд по тому же тексту, что и предпросмотр.
        deptCode: routed && manualDept && preview.result?.routedBy === 'manual' ? manualDept : undefined,
      });
      // Вложения — после создания: обращение уже есть, даже если файл не дошёл.
      let failed = 0;
      for (const f of attachments) {
        await crmFeedbackApi.attach(r.id, f, f.name, kind === 'bug' && f.type.startsWith('image/') ? 'screenshot' : 'file').catch(() => (failed += 1));
      }
      if (failed) message.warning(`Обращение отправлено, но не приложилось файлов: ${failed}`);
      void qc.invalidateQueries({ queryKey: ['wiki', 'feedback'] });
      // Куда ушёл вопрос — по данным сервера (он мог определить отдел чуть иначе, чем предпросмотр).
      let question: SentInfo['question'];
      if (routed) {
        const d = await crmFeedbackApi.myOne(r.id).catch(() => null);
        if (d?.deptLabel) {
          // Срок в минутах знает только предпросмотр — берём его, если отдел тот же («в течение часа» у срочных).
          const slaMinutes = preview.result?.deptLabel === d.deptLabel ? preview.result.slaMinutes : null;
          question = { deptLabel: d.deptLabel, assigneeName: d.assigneeName ?? null, dueAt: d.dueAt ?? null, urgent: !!d.urgent, slaMinutes };
        }
      }
      sentRef.current = true;
      clearFeedbackDraft(draftKey);
      onSent({ ...r, question });
    } catch (e) {
      message.error((e as Error).message || 'Не удалось отправить обращение');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fk-hub-fb-form" onPaste={onPaste}>
      <div className="fk-hub-fb-cols" data-full={full ? 'true' : 'false'}>
        <div>
          <div className="fk-hub-fb-label">{LABEL[kind]}</div>
          <textarea
            autoFocus
            className="fk-hub-fb-input fk-hub-fb-textarea"
            data-full={full ? 'true' : 'false'}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={PLACEHOLDER[kind]}
            maxLength={5000}
            onKeyDown={(e) => {
              // Ctrl+Enter / ⌘+Enter — отправить (Enter в поле — просто новая строка).
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                void submit();
              }
            }}
          />
          {restored ? <div className="fk-hub-fb-hint">Восстановлен черновик — обращение ещё не отправлено, нажмите «Отправить».</div> : null}
          {routed && (preview.result || preview.loading) ? (
            <div className="fk-hub-fb-gap">
              <RoutingCard preview={preview} manualDept={manualDept} onPickDept={setManualDept} onResetDept={() => setManualDept(null)} />
            </div>
          ) : null}
          {kind === 'bug' ? (
            <>
              <div className="fk-hub-fb-gap">
                <div className="fk-hub-fb-label">Что ожидали</div>
                <textarea
                  className="fk-hub-fb-input fk-hub-fb-textarea fk-hub-fb-textarea--sm"
                  data-full={full ? 'true' : 'false'}
                  value={expected}
                  onChange={(e) => setExpected(e.target.value)}
                  placeholder="Файл откроется в новой вкладке"
                  maxLength={3000}
                />
              </div>
              <div className="fk-hub-fb-gap">
                <div className="fk-hub-fb-label">Насколько мешает</div>
                <div className="fk-hub-fb-sev">
                  {(Object.keys(FB_SEVERITY) as FeedbackSeverity[]).map((k) => (
                    <button type="button" key={k} className="fk-hub-fb-sev-btn" aria-pressed={sev === k} onClick={() => setSev(k)}>
                      <span className="fk-hub-fb-sev-name">
                        <span className="fk-hub-fb-sev-dot" style={{ background: FB_SEVERITY[k].tone }} />
                        {FB_SEVERITY[k].label}
                      </span>
                      <span className="fk-hub-fb-sev-hint">{FB_SEVERITY[k].hint}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div>
          {kind === 'bug' ? <div className="fk-hub-fb-label">Снимок экрана</div> : null}
          {shotSrc ? (
            <div className="fk-hub-fb-gap-b">
              <ScreenshotAnnotator
                src={shotSrc}
                label={where.page}
                onChange={setShotBlob}
                onRemove={() => {
                  setShotSrc(null);
                  setShotBlob(null);
                }}
              />
            </div>
          ) : null}
          <div className="fk-hub-fb-tools">
            {kind === 'bug' && !shotSrc && canCaptureScreen() ? (
              <button type="button" className="fk-hub-fb-tool" onClick={() => void takeShot()} disabled={shooting || noSlots}>
                <IconCamera size={13} />
                Сделать снимок экрана
              </button>
            ) : null}
            <button type="button" className="fk-hub-fb-tool" onClick={() => fileRef.current?.click()} disabled={noSlots}>
              <IconPaperclip size={13} />
              {kind === 'bug' ? 'Загрузить снимок или файл' : 'Приложить картинку или PDF'}
            </button>
          </div>
          {kind === 'bug' && !shotSrc && canCaptureScreen() ? (
            <div className="fk-hub-fb-hint">Браузер спросит, какую вкладку показать — выберите эту. Потом обведите место ошибки.</div>
          ) : null}
          <div className="fk-hub-fb-hint">{kind === 'bug' ? 'Можно вставить снимок из буфера: Ctrl+V. PNG, JPG, WEBP или PDF до 10 МБ.' : 'PNG, JPG, WEBP или PDF до 10 МБ. Картинку можно вставить из буфера: Ctrl+V.'}</div>
          {files.length ? (
            <div className="fk-hub-fb-files">
              {files.map((f, i) => (
                <FileRow key={`${f.name}-${i}`} file={f} onRemove={() => setFiles((x) => x.filter((_, j) => j !== i))} />
              ))}
            </div>
          ) : null}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            multiple={maxFiles > 1}
            hidden
            onChange={(e) => {
              addFiles(Array.from(e.target.files ?? []));
              e.target.value = '';
            }}
          />
        </div>
      </div>

      <div className="fk-hub-fb-foot">
        <button type="button" className="fk-hub-fb-send" disabled={busy} onClick={() => void submit()}>
          <IconSend size={14} />
          {busy ? 'Отправляем…' : 'Отправить'}
        </button>
        <span className="fk-hub-fb-foot-note">{routed && preview.result ? previewFooterText(preview.result) : sla}</span>
      </div>
    </div>
  );
}
